import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  MenuItem,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import {
  AutoDelete as AutoDeleteIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '../../firebase/config';
import { selectCanManageGlobalSettings } from '../../redux/slices/authSlice';
import {
  ADJUSTABLE_RETENTION,
  FIXED_RETENTION,
  MIETWAGEN_MIN_RIDE_RETENTION_DAYS,
  resolveRetentionSettings
} from '../../config/dataRetention';

const DeletionStatus = ({ autoDelete }) => (
  autoDelete ? (
    <Chip
      size="small"
      color="success"
      variant="outlined"
      icon={<AutoDeleteIcon />}
      label="Wird automatisch gelöscht"
    />
  ) : (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      icon={<PendingIcon />}
      label="Automatische Löschung folgt — bis dahin gespeichert"
    />
  )
);

const formatDateTime = (timestamp) => {
  const date = timestamp?.toDate?.();
  return date ? date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : null;
};

/**
 * Einstellungen → „Datenschutz & Aufbewahrung" (Löschkonzept Kap. 8).
 * Zeigt jede Datenart mit Frist, Rechtsgrundlage und ehrlichem Löschstatus.
 * Ändern dürfen nur zentrale Admins, und nur innerhalb der gesetzlichen
 * Grenzen — die Callable `configureDataRetention` prüft sie serverseitig.
 */
const DataRetentionSettingsSection = () => {
  const canManage = useSelector(selectCanManageGlobalSettings);
  const unternehmensform = useSelector(state => state.settings.global?.unternehmensform) === 'mietwagen'
    ? 'mietwagen'
    : 'taxi';
  const cloudFunctionSettings = useSelector(state => state.settings.cloudFunctions);
  const [retentionDoc, setRetentionDoc] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'dataRetention'),
      (snapshot) => setRetentionDoc(snapshot.exists() ? snapshot.data() : {}),
      (error) => {
        console.error('Aufbewahrungsfristen konnten nicht geladen werden:', error);
        setRetentionDoc({});
      }
    );
    return unsubscribe;
  }, []);

  const values = useMemo(
    () => resolveRetentionSettings(retentionDoc, cloudFunctionSettings, unternehmensform),
    [retentionDoc, cloudFunctionSettings, unternehmensform]
  );

  const handleChange = async (key, value) => {
    setSavingKey(key);
    try {
      await httpsCallable(functions, 'configureDataRetention')({ [key]: value });
      setSnackbar({ open: true, message: 'Aufbewahrungsfrist gespeichert', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: error?.message || 'Speichern fehlgeschlagen', severity: 'error' });
    } finally {
      setSavingKey(null);
    }
  };

  const lastChanged = formatDateTime(retentionDoc?.updatedAt);

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          Für diese Daten ist Ihr Betrieb verantwortlich: Sie müssen so lange aufbewahrt werden,
          wie es das Gesetz verlangt — und danach gelöscht werden (DSGVO). Die Plattform löscht
          nach Ablauf der Frist automatisch. Gesetzliche Mindestfristen lassen sich nicht
          unterschreiten.
          {unternehmensform === 'mietwagen' && (
            <> Als Mietwagenbetrieb gilt für Fahrten mindestens 1 Jahr (§ 49 Abs. 4 PBefG).</>
          )}
        </Alert>
      </Box>

      <Box sx={{ px: 2, pt: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">Einstellbare Fristen</Typography>
        {!canManage && (
          <Typography variant="caption" color="text.secondary">
            Nur die Zentrale kann diese Fristen ändern.
          </Typography>
        )}
      </Box>
      <List disablePadding>
        {ADJUSTABLE_RETENTION.map((config) => (
          <ListItem
            key={config.key}
            divider
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1">{config.label}</Typography>
              <Typography variant="body2" color="text.secondary">{config.description}</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                {config.legal(unternehmensform)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DeletionStatus autoDelete={config.autoDelete} />
              </Box>
            </Box>
            <TextField
              select
              size="small"
              label="Aufbewahrung"
              value={values[config.key]}
              disabled={!canManage || savingKey === config.key || retentionDoc === null}
              onChange={(event) => handleChange(config.key, Number(event.target.value))}
              sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
            >
              {config.options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={
                    config.key === 'rideRetentionDays' &&
                    unternehmensform === 'mietwagen' &&
                    option.value < MIETWAGEN_MIN_RIDE_RETENTION_DAYS
                  }
                >
                  {option.label}
                  {option.value === config.defaultValue(unternehmensform) ? ' (Standard)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </ListItem>
        ))}
      </List>

      <Box sx={{ px: 2, pt: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Feste Fristen</Typography>
        <Typography variant="caption" color="text.secondary">
          Diese Fristen sind gesetzlich oder technisch vorgegeben und nicht änderbar.
        </Typography>
      </Box>
      <List disablePadding>
        {FIXED_RETENTION.map((entry) => (
          <ListItem
            key={entry.label}
            divider
            sx={{
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1">{entry.label}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {entry.legal}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.5, flexShrink: 0, maxWidth: { sm: 320 } }}>
              <Typography variant="body2" fontWeight={500} sx={{ textAlign: { sm: 'right' } }}>
                {entry.period}
              </Typography>
              <DeletionStatus autoDelete={entry.autoDelete} />
            </Box>
          </ListItem>
        ))}
      </List>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" component="div">
          Jahresfristen laufen ab dem Ende des Kalenderjahres (Beispiel: Quittung aus 2026,
          8 Jahre → gelöscht ab 01.01.2035).
          {lastChanged ? ` Zuletzt geändert: ${lastChanged}.` : ''}
        </Typography>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataRetentionSettingsSection;
